import { useState } from 'react';
import type { Message } from '../api/client';
import { FLAG_REASONS } from '../api/moderation';
import type { FlagReason, ModerationTransport } from '../api/moderation';
import { Button } from '../ui/Button';
import { TextAreaField } from '../ui/Field';
import { Overlay } from '../ui/Overlay';

/**
 * What this reader can do with one message — §5, TROOP-12.
 *
 * **Reporting is offered to everybody, and that is the feature.** §3 gives flagging to every member
 * including youth, and §5's argument is that the Scout who is uncomfortable but will not object in
 * front of their patrol needs a way to say so without a confrontation. So there is no standing test
 * on the report control: if you can read the thread, you can report what is in it.
 *
 * **Taking a message down is gated on the server's own per-message answer** (`canRemove` /
 * `canRestore`), which `ModerationPolicy` computes per row. Nothing here re-derives it. A control
 * that is simply absent is the right shape for this one: the alternative is a disabled button owing
 * an explanation, and "you did not write this" is not a reason anybody needs stated (spec §6 asks
 * for the explanation where there is one to give, and says a control with nothing useful to say
 * probably should not render).
 *
 * **A refusal renders the server's own sentence.** The API writes its refusals for the person
 * reading them — *"this report is about something you wrote, so somebody else has to answer it"* —
 * and paraphrasing them here would be a second copy of a rule this file does not hold.
 *
 * **SPEC-GAP:** `handoff/design-system.md` lists no action sheet, so this is `Overlay` (the only
 * modal, per CLAUDE.md) plus existing tokens and `Button`. No new colour, no new elevation.
 */
export function MessageActions({
  message,
  transport,
  onClose,
  onChanged,
}: {
  message: Message;
  transport: ModerationTransport;
  onClose: () => void;
  /** Re-reads the thread, so the tombstone appears without a reload. */
  onChanged: () => void;
}) {
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState<FlagReason>('YptConcern');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [removedBody, setRemovedBody] = useState<string | null>(null);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay title="This message" onClose={onClose}>
      <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {message.authorDisplayName}
          {message.isRemoved ? ' · taken down' : ''}
        </p>

        {done ? (
          <p className="t-body" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
            {done}
          </p>
        ) : reporting ? (
          <>
            <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
              <legend className="t-label" style={{ color: 'var(--color-text-muted)' }}>
                What is wrong with it
              </legend>

              <div
                className="flex flex-col"
                style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}
              >
                {FLAG_REASONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex gap-3"
                    style={{
                      alignItems: 'flex-start',
                      minHeight: 44,
                      padding: 'var(--space-2)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="flag-reason"
                      value={option.value}
                      checked={reason === option.value}
                      onChange={() => setReason(option.value)}
                      style={{ marginTop: 4 }}
                    />
                    <span>
                      <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
                        {option.label}
                      </span>
                      <br />
                      <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                        {option.hint}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <TextAreaField
              label="Anything you want to add"
              hint="Whoever looks at this will read it."
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              The person who wrote this will not be told you reported it. You will be able to see
              what happens to it.
            </p>

            <Button
              fullWidth
              onClick={() =>
                void run(async () => {
                  await transport.raise(message.id, reason, note.trim() || null);
                  setDone(
                    'Reported. You can follow what happens to it under Reports, and nobody has been told who raised it.',
                  );
                  onChanged();
                })
              }
              disabledReason={busy ? 'Sending your report.' : undefined}
            >
              Send this report
            </Button>

            <Button variant="secondary" fullWidth onClick={() => setReporting(false)}>
              Back
            </Button>
          </>
        ) : (
          <>
            <Button fullWidth variant="secondary" onClick={() => setReporting(true)}>
              Report this message
            </Button>

            {/* The server's own per-row answers. Absent rather than disabled: "you did not write
                this" is not an explanation worth a control (spec §6). */}
            {message.canRemove && (
              <Button
                fullWidth
                variant="destructive"
                onClick={() =>
                  void run(async () => {
                    await transport.setRemoved(message.id, true);
                    setDone('Taken down. The thread still shows that it was here, and who wrote it.');
                    onChanged();
                  })
                }
                disabledReason={busy ? 'Taking it down.' : undefined}
              >
                Take it down
              </Button>
            )}

            {message.canRestore && (
              <Button
                fullWidth
                variant="secondary"
                onClick={() =>
                  void run(async () => {
                    await transport.setRemoved(message.id, false);
                    setDone('Back in the thread.');
                    onChanged();
                  })
                }
                disabledReason={busy ? 'Putting it back.' : undefined}
              >
                Put it back
              </Button>
            )}

            {/* §5: unit admins, and the author their own. Offered to everybody rather than gated,
                because the refusal is the honest answer and says who can — and because a body that
                arrived with the page for the people entitled to it would be one refactor away from
                arriving for everybody. */}
            {message.isRemoved && removedBody === null && (
              <Button
                fullWidth
                variant="secondary"
                onClick={() =>
                  void run(async () => {
                    const { body } = await transport.removedBody(message.id);
                    setRemovedBody(body ?? '(nothing was stored)');
                  })
                }
                disabledReason={busy ? 'Looking it up.' : undefined}
              >
                Read what it said
              </Button>
            )}

            {removedBody !== null && (
              <div
                style={{
                  border: '1px dashed var(--color-border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3) var(--space-4)',
                }}
              >
                <p
                  className="t-label"
                  style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
                >
                  What it said
                </p>
                <p
                  className="t-body"
                  style={{ color: 'var(--color-text-primary)', margin: 0, overflowWrap: 'anywhere' }}
                >
                  {removedBody}
                </p>
              </div>
            )}
          </>
        )}

        {error && (
          <p
            className="t-caption"
            role="alert"
            style={{ color: 'var(--color-status-blocked)', margin: 0 }}
          >
            {error}
          </p>
        )}

        <Button variant="secondary" fullWidth onClick={onClose}>
          {done ? 'Done' : 'Close'}
        </Button>
      </div>
    </Overlay>
  );
}
